import {Card} from "@material-ui/core";
import CardContent from "@material-ui/core/CardContent";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useAlert} from "react-alert";
import {useHistory, useParams} from "react-router-dom";
import {allDestinationsData, DestinationDefinition, destinationKeyToSlugMap} from "../destinations/destinations";
import {
    DestinationKey,
    DestinationStandardMappingItemNamed,
    DestinationStandardMappingItemProperty,
} from "../destinations/types";
import {destinationIsValidForEvent, eventDefType, libraryForEventType} from "../eventTypes";
import useEnvironment from "../hooks/use-environment";
import Navigation from "../Navigation";
import {LoadedDestinationInfo, LoadedEventDefintion} from "../types/global";
import {makeRequest} from "../util";
import {
    ALL_DESTINATIONS_VALUE,
    ALL_EVENTS_VALUE,
    NewTransformationPageStep2,
} from "./components/creation/destination-and-event-def-selection";
import {NewTransformationPageStep1} from "./components/creation/intention-selection";
import {NewTransformationPageStep3} from "./components/creation/standard-event-prop-mapping";
import {NewTransformationPageStepNavigation} from "./components/creation/step-navigation";
import {TransformDefinitionSet} from "./transformationDefinition";
import {
    GetTransformationSetsResponse,
    LoadedTransformationSet,
    TransformationIntention,
    TransformationOperationType,
} from "./types";
import {useDispatch, useSelector} from "react-redux";
import {fetchEventDefs} from "../actions/eventDefs";


type UrlIntention = "fix-bad-data" | "standard-event";
const urlIntentions: Record<UrlIntention, TransformationIntention> = {
    "fix-bad-data": "fixBadData",
    "standard-event": "standardEvent",
};

const NewTransformationPage = () => {
    let intention: UrlIntention | undefined;
    const params = useParams<{ intention: UrlIntention }>();
    intention = params.intention;

    const alert = useAlert();
    const history = useHistory();
    const currentEnvironment = useEnvironment();
    const [transformationData, setTransformationData] = useState<Partial<LoadedTransformationSet>>({});
    const [step, setStep] = useState(1);
    const [availableDestsIDToKey, setAvailableDestsIDToKey] = useState<Record<string, DestinationKey>>({});
    const [existingTransformationSets, setExistingTransformationSets] = useState<TransformDefinitionSet[]>([]);
    const [propertyMapping, setPropertyMapping] = useState<Record<string, string>>({});
    const [selectedDestinationStandardEvent, setSelectedDestinationStandardEvent] = useState<DestinationStandardMappingItemNamed | null>(null);
    const [selectedDestinationDefinition, setSelectedDestinationDefinition] = useState<DestinationDefinition | null>(null);
    const [selectedDestinationConfig, setSelectedDestinationConfig] = useState<LoadedDestinationInfo | null>(null);

    const eventDefinitions = useSelector((state: { eventDefs: { eventDefs: LoadedEventDefintion[] } }) => state.eventDefs.eventDefs);
    const destinationData = useSelector((state: { destinations: { data: LoadedDestinationInfo[] } }) => state.destinations.data);
    const destinations = useMemo(() => Object.values(destinationData), [destinationData]);

    const updateTransformationData = (key: keyof LoadedTransformationSet, fromReactSelect: boolean) => (value: any) => {
        const val = fromReactSelect ? value.value : value;
        setTransformationData({...transformationData, ...{[key]: val}});
        setSelectedDestinationStandardEvent(null);
        setPropertyMapping({});
    };

    const getExistingTransformationSets = () => {
        makeRequest<any>("get_transformation_sets", {})
            .then((response: { data: GetTransformationSetsResponse }) => {
                setExistingTransformationSets(_.map(response.data, (setData) => new TransformDefinitionSet(setData)));
            })
            .catch((error: string) => {
                alert.error("Something went wrong. Unable to load page.");
                console.log(error);
                Sentry.captureException(error);
            });
    };

    const findDuplicateTransformationSet = () => {
        // Cannot have the same event + definition for multiple transformation sets.
        return _.find(existingTransformationSets, (ts) => ts.eventDefId === transformationData.eventDefId && ts.destinationName === transformationData.destinationName) || null;
    };

    useEffect(() => {
        if (transformationData.destinationName) {
            const selectedDestinationSlug = destinationKeyToSlugMap[transformationData.destinationName];
            const destConfig = _.find(destinations, (dest) => dest.name == transformationData.destinationName);
            if (destConfig) {
                setSelectedDestinationConfig(destConfig);
            }

            setSelectedDestinationDefinition(allDestinationsData[selectedDestinationSlug]);
        }
    }, [transformationData, destinations]);

    useEffect(() => {
        const props: Partial<LoadedTransformationSet> = {};
        if (intention) {
            props["intention"] = urlIntentions[intention];
            setStep(Math.max(step, 2));
        }
        setTransformationData({...transformationData, ...props});
    }, [intention]);

    const refreshEventDefinitions = () => {
        if (eventDefinitions && destinations) {
            const tmpDestsIDToKey: Record<string, DestinationKey> = {};

            _.forEach(destinations, (destInfo: LoadedDestinationInfo) => {
                tmpDestsIDToKey[destInfo.id] = destInfo.name;
            });
            setAvailableDestsIDToKey(tmpDestsIDToKey);
        }
    };

    const dispatch = useDispatch();
    const triggerReload = useCallback(() => dispatch(fetchEventDefs()), []);

    useEffect(() => {
        refreshEventDefinitions()
    }, [eventDefinitions, destinations]);

    useEffect(() => {
        refreshEventDefinitions();
        getExistingTransformationSets();
    }, [currentEnvironment.ID]);

    const setIntention = (intention: TransformationIntention) => {
        history.replace(`/transformations/new/${_.invert(urlIntentions)[intention]}`);
        setStep(2);

        if (transformationData.intention != intention) {
            setTransformationData({
                ...transformationData,
                intention,
                eventDefId: undefined,
                destinationName: undefined,
            });
            setSelectedDestinationStandardEvent(null);
            setPropertyMapping({});
            setSelectedDestinationDefinition(null);
        }
    };

    const changeSelectedDestinationStandardEvent = React.useCallback((standardEvent: DestinationStandardMappingItemNamed | null) => {
        setSelectedDestinationStandardEvent(standardEvent);
        if (findDuplicateTransformationSet()) {
            return;
        }
        if (standardEvent) {
            setStep(3);
        } else {
            setStep(2);
        }
    }, []);

    const onSaveStandardEvent = () => {
        if (!selectedDestinationStandardEvent) {
            return;
        }

        let convertedTransformations: Array<{
            operation: TransformationOperationType;
            targetValue?: string;
            operandValue?: any;
        }> = [{
            operation: "replaceName",
            operandValue: selectedDestinationStandardEvent.eventName,
        }];
        if (selectedDestinationStandardEvent) {
            convertedTransformations = _.concat(
                convertedTransformations,
                _.chain(selectedDestinationStandardEvent.propertiesArr)
                    .map((prop: DestinationStandardMappingItemProperty) => {
                        const results = [];

                        if (propertyMapping[prop.key] !== "" && propertyMapping[prop.key] !== prop.key) {
                            results.push({
                                targetValue: propertyMapping[prop.key],
                                operation: "replacePropertyKey" as TransformationOperationType,
                                operandValue: prop.key,
                            });
                        }

                        if (prop.additionalTransformation) {
                            results.push({
                                targetValue: propertyMapping[prop.key],
                                operation: prop.additionalTransformation.operation,
                                operandValue: prop.additionalTransformation.operandValue,
                            });
                        }
                        return results;
                    })
                    .flatten()
                    .value(),
            );
        }

        makeRequest<any>("save_new_transformation_group", {
            intention: transformationData.intention,
            destinationName: transformationData.destinationName,
            eventDefId: transformationData.eventDefId,
            transformations: convertedTransformations,
        }).then((response) => {
            if (response.data.errorMessage) {
                if (response.data.errorMessage.indexOf("unique constraint")) {
                    alert.error("You already have an existing transformation for this destination + event combination.");
                }
                alert.error("Something went wrong");
                console.error(response.data.errorMessage);
            } else {
                history.push(`/transformations/edit/${response.data.transformationSetId}`);
            }
        });
    };

    const onSaveFixBadData = () => {
        // @ts-ignore
        const destinationName = transformationData.destinationName === ALL_DESTINATIONS_VALUE ? null : transformationData.destinationName;
        const eventDefId = transformationData.eventDefId === ALL_EVENTS_VALUE ? null : transformationData.eventDefId;
        makeRequest<any>("save_new_transformation_group", {
            intention: transformationData.intention,
            destinationName: destinationName,
            eventDefId: eventDefId,
            transformations: [],
        }).then((response) => {
            history.push(`/transformations/edit/${response.data.transformationSetId}`);
        });
    };

    const selectedEventDefConfig = _.find(
        eventDefinitions,
        (def) => def.id === transformationData.eventDefId,
    );

    const showSaveButton =
        transformationData.intention === "standardEvent" ? step >= 3 :
            (step >= 2 && transformationData.destinationName && transformationData.eventDefId);

    const allDestinationsSelected = transformationData.destinationName === ALL_DESTINATIONS_VALUE;
    const allEventDefsSelected = transformationData.eventDefId === ALL_EVENTS_VALUE;

    // Valid combination if one or neither "event" or "destination" is "ALL"
    const validCombination = !(allEventDefsSelected && allDestinationsSelected);

    // Valid if destinations is "ALL" or there's a good destination config
    const validDestinationConfig = (allDestinationsSelected || selectedDestinationConfig);

    // Valid if event def is "ALL" or there's a good event def config
    const validEventDefConfig = (allEventDefsSelected || selectedEventDefConfig);

    // Valid if event def is "ALL" or destinationIsValidForEvent
    const validEventForDest = validEventDefConfig && validDestinationConfig && (allEventDefsSelected || destinationIsValidForEvent(
        selectedDestinationConfig,
        libraryForEventType(eventDefType(selectedEventDefConfig)) !== "web",
    ));

    const isButtonEnabled = !!(
        showSaveButton &&
        validCombination &&
        validDestinationConfig &&
        validEventDefConfig &&
        validEventForDest
    );

    const saveHandler = transformationData.intention === "standardEvent" ? onSaveStandardEvent : onSaveFixBadData;

    return (
        <Card className="my-4 min-h-full" style={{overflow: "visible"}}>
            <CardContent className="w-full pt-4 min-h-full">
                <NewTransformationPageStepNavigation step={step}/>
                <div className="w-full border-b border-color-gray-300 mt-4  "/>

                {step >= 1 &&
                <NewTransformationPageStep1
                    intention={transformationData.intention}
                    setIntention={setIntention}
                />}

                {step >= 2 && transformationData.intention &&
                <NewTransformationPageStep2
                    eventDefinitions={eventDefinitions}
                    intention={transformationData.intention}
                    duplicateTransformationSet={findDuplicateTransformationSet()}
                    selectDestination={updateTransformationData("destinationName", true)}
                    selectedDestination={transformationData.destinationName}
                    selectedDestinationDefinition={selectedDestinationDefinition}
                    selectedDestinationConfig={selectedDestinationConfig}
                    selectedDestinationStandardEvent={selectedDestinationStandardEvent}
                    setSelectedDestinationStandardEvent={changeSelectedDestinationStandardEvent}
                    selectEventDef={updateTransformationData("eventDefId", true)}
                    selectedEventDef={transformationData.eventDefId}
                    selectedEventDefConfig={selectedEventDefConfig}
                    availableDestsIDToKey={availableDestsIDToKey}
                    setPropertyMapping={setPropertyMapping}
                    propertyMapping={propertyMapping}
                    refreshEventDefinitions={triggerReload}
                />}

                {step >= 3 && selectedDestinationDefinition && selectedDestinationStandardEvent && (
                    <NewTransformationPageStep3
                        setPropertyMapping={setPropertyMapping}
                        propertyMapping={propertyMapping}
                        selectedDestinationDefinition={selectedDestinationDefinition}
                        selectedDestinationStandardEvent={selectedDestinationStandardEvent}
                    />
                )}

                {showSaveButton && (
                    <div className="flex justify-center">
                        <div className="w-1/3">
                            <button
                                disabled={!isButtonEnabled}
                                onClick={saveHandler}
                                className={clsx("cursor-pointer w-full btn p-2 rounded mt-2 text-white text-center",
                                    isButtonEnabled ? "bg-turquoise-700 hover:bg-turquoise-900" : "bg-turquoise-500 hover:bg-turquoise-500")}>
                                {transformationData.intention === "standardEvent" ?
                                    "Save" : "Save & Add Transformations"
                                }
                            </button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};


function NewTransformationPageWithNavigation() {
    return (
        <Navigation className="min-h-full">
            <NewTransformationPage/>
        </Navigation>
    );
}

export default NewTransformationPageWithNavigation;
